import { AwardWinners } from "../definitions";

type OtherAwards = Record<string, AwardWinners> | undefined;

/** Normalises the legacy single-name form and the shared-award array form to one list. */
export const getAwardWinners = (value: AwardWinners | undefined | null): string[] => {
  if (!value) return [];
  const names = Array.isArray(value) ? value : [value];
  return names.filter((name): name is string => typeof name === 'string' && name.trim() !== '');
};

/** Flattened replacement for `Object.values(result.otherAwards)`. */
export const getAllAwardWinners = (otherAwards: OtherAwards): string[] =>
  Object.values(otherAwards ?? {}).flatMap(getAwardWinners);

/** Case-insensitive — matches the comparison most award consumers already use. */
export const awardWinnersInclude = (value: AwardWinners | undefined, playerName: string): boolean => {
  const target = playerName.trim().toLowerCase();
  if (!target) return false;
  return getAwardWinners(value).some(name => name.trim().toLowerCase() === target);
};

/** Exact match — for scout tables that compare raw stored names. */
export const awardWinnersIncludeExact = (value: AwardWinners | undefined, playerName: string): boolean =>
  getAwardWinners(value).includes(playerName);

export const countAwardWinsFor = (otherAwards: OtherAwards, playerName: string): number => {
  const target = playerName.trim().toLowerCase();
  if (!target) return 0;
  return getAllAwardWinners(otherAwards).filter(name => name.trim().toLowerCase() === target).length;
};

export const countAwardWinsForExact = (otherAwards: OtherAwards, playerName: string): number =>
  getAllAwardWinners(otherAwards).filter(name => name === playerName).length;

/** Award names this player won in a single result. */
export const getAwardNamesWonBy = (otherAwards: OtherAwards, playerName: string): string[] =>
  Object.entries(otherAwards ?? {})
    .filter(([, winners]) => awardWinnersInclude(winners, playerName))
    .map(([awardName]) => awardName);

export const formatAwardWinners = (value: AwardWinners | undefined): string => {
  const winners = getAwardWinners(value);
  if (winners.length <= 1) return winners[0] ?? '';
  return `${winners.slice(0, -1).join(', ')} & ${winners[winners.length - 1]}`;
};

/** Collapses back to a plain string when there is a single winner, keeping legacy data shape. */
export const toAwardWinners = (winners: string[]): AwardWinners =>
  winners.length === 1 ? winners[0] : winners;

export const replaceAwardWinner = (value: AwardWinners | undefined, fromLower: string, to: string): AwardWinners => {
  const renamed = getAwardWinners(value).map(name => (name.toLowerCase() === fromLower ? to : name));
  return Array.isArray(value) ? renamed : (renamed[0] ?? '');
};
